import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { cart, cartItemKey, cartSubtotal, useCart } from '../lib/cart.js';
import { useShop } from '../lib/shop-context.js';
import { isMiniApp, setMainButton } from '../lib/telegram.js';
import { ShopHeader, StickyBar } from '../components/chrome.js';
import { EmptyState, Money } from '../components/primitives.js';

/** The cart. Prices shown here are indicative; the server re-prices at quote
 *  and again at checkout, so the number on the QR is always authoritative. */
export function CartPage() {
  const { data, t } = useShop();
  const shop = data.shop;
  const { items } = useCart();
  const navigate = useNavigate();
  const subtotal = cartSubtotal(items);

  useEffect(() => {
    if (!isMiniApp() || items.length === 0) return;
    return setMainButton({
      text: t('storefront.checkout'),
      onClick: () => void navigate({ to: '/s/$slug/checkout', params: { slug: shop.slug } }),
    });
  });

  if (items.length === 0) {
    return (
      <div className="min-h-dvh">
        <ShopHeader showBack />
        <EmptyState
          title={t('storefront.cartEmpty')}
          action={
            <Link to="/s/$slug" params={{ slug: shop.slug }} className="btn btn-primary mt-2">
              {t('storefront.continueShopping')}
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="min-h-dvh pb-32">
      <ShopHeader showBack />

      <ul className="px-4 pt-4">
        {items.map((item) => {
          const key = cartItemKey(item);
          return (
            <li
              key={key}
              className="flex gap-3 py-4"
              style={{ borderBottom: '1px solid var(--color-border)' }}
            >
              <div
                className="size-20 shrink-0 overflow-hidden rounded-[var(--radius-card)]"
                style={{ background: 'var(--color-surface)' }}
              >
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt="" className="size-full object-cover" />
                ) : null}
              </div>

              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <p className="line-clamp-2 text-sm font-medium">{item.name}</p>
                {item.options ? <p className="text-xs opacity-60">{item.options}</p> : null}
                <Money amount={item.unitPrice} currency={shop.currency} className="text-sm font-semibold" />

                <div className="mt-1 flex items-center gap-3">
                  <div
                    className="flex items-center overflow-hidden rounded-[var(--radius)]"
                    style={{ border: '1px solid var(--color-border)' }}
                  >
                    <button
                      type="button"
                      className="px-3 py-1.5"
                      aria-label="−"
                      onClick={() => cart.setQuantity(key, item.quantity - 1)}
                    >
                      −
                    </button>
                    <span className="min-w-8 text-center text-sm">{item.quantity}</span>
                    <button
                      type="button"
                      className="px-3 py-1.5"
                      aria-label="+"
                      onClick={() => cart.setQuantity(key, item.quantity + 1)}
                    >
                      +
                    </button>
                  </div>
                  <button
                    type="button"
                    className="text-xs opacity-60 underline"
                    onClick={() => cart.remove(key)}
                  >
                    {t('common.delete')}
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="flex items-center justify-between px-4 pt-6">
        <span className="text-sm opacity-70">{t('common.subtotal')}</span>
        <Money amount={subtotal} currency={shop.currency} className="text-lg font-bold" />
      </div>
      <p className="px-4 pt-1 text-xs opacity-55">{t('checkout.selectProvinceFirst')}</p>

      <StickyBar>
        <Link
          to="/s/$slug/checkout"
          params={{ slug: shop.slug }}
          className="btn btn-primary w-full"
        >
          {t('storefront.checkout')}
        </Link>
      </StickyBar>
    </div>
  );
}
